import { AccessIcon, EmptyState, LazyLoader } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { TimetableVersionLessonProvider } from '../../../../components/Lessons/TimetableVersion/TimetableVersionLessonProvider';
import { Page } from '../../../../components/Page/Page';
import { TimetableProvider } from '../../../../components/TimetableGrid/TimetableProvider';
import { TimetableVersionTopBar } from '../../../../components/TimetableVersion/TimetableVersionTopBar/TimetableVersionTopBar';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { AvailabilityStore } from '../../../../stores/AvailabilityStore';
import { StoreHandler } from '../../../../stores/StoreHandler';
import { TimeAvailabilityStore } from '../../../../stores/TimeAvailabilityStore';
import {
  ClassGridCardsQuery,
  use_TimeGridEntriesQuery,
  use_TimetablesQuery,
} from '../../../../types/planung-graphql-client-defs';

export const timeAvailabilityStoreHandler = new StoreHandler<TimeAvailabilityStore>();
export const availabilityStore = new AvailabilityStore();
export type ClassGridCards = ClassGridCardsQuery['classGridCards'][0];

export const TimetableVersion = observer(({ baseSlug }: { baseSlug: string }) => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();
  const permissions = usePermissionChecker();

  const [{ data: timetableData }] = use_TimetablesQuery({
    variables: {
      where: {
        versions_SOME: { uuid: versionUuid },
      },
    },
    pause: !versionUuid,
  });

  const timegridUuid = timetableData?.timetables[0].timegridConfigsConnection.edges[0].node.timegrid?.uuid;

  const [{ data: timeGridEntriesData }] = use_TimeGridEntriesQuery({
    variables: {
      where: {
        timeGrid: {
          uuid: timegridUuid,
        },
      },
    },
    pause: !timegridUuid,
  });

  const schoolYear = useUserConfigContext();

  const [ready, setReady] = useState(false);
  const [inSchoolYear, setInSchoolYear] = useState<boolean>(false);

  useEffect(() => {
    if (versionUuid) {
      const inSchoolYear = timetableData?.timetables[0].schoolYear.uuid === schoolYear.selectedSchoolYear?.uuid;
      setInSchoolYear(inSchoolYear);
    }
  }, [versionUuid, timetableData, schoolYear.selectedSchoolYear]);

  useEffect(() => {
    if (versionUuid && timetableData && timeGridEntriesData) {
      timeAvailabilityStoreHandler.register(versionUuid, () => {
        return new TimeAvailabilityStore('00:00', '24:00', undefined, timeGridEntriesData.timeGridEntries);
      });
      setReady(true);
    }
  }, [timetableData, versionUuid, timeGridEntriesData]);

  return (
    <Page isForbidden={!permissions?.canReadTimetable()} fullWidth fullHeight>
      {versionUuid && ready && (
        <>
          {inSchoolYear ? (
            <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='60vh' />}>
              <TimetableVersionLessonProvider
                organizationUuid={organizationUuid}
                schoolYearUuid={schoolYear.selectedSchoolYear?.uuid ?? ''}
              >
                <TimetableProvider versionUuid={versionUuid}>
                  <TimetableVersionTopBar versionUuid={versionUuid} baseSlug={baseSlug} />
                  <Outlet />
                </TimetableProvider>
              </TimetableVersionLessonProvider>
            </Suspense>
          ) : (
            <EmptyState
              icon={<AccessIcon color='var(--color-error)' />}
              title={t('timetableVersion.notInSchoolYear')}
              forcedHeight='60vh'
            />
          )}
        </>
      )}
    </Page>
  );
});
